import type { ReactElement, ReactNode } from 'react';
import { useEffect, useRef, useState } from 'react';

import isNil from 'lodash/isNil';
import { useTranslation } from 'react-i18next';

import type { Config } from '../../../config';
import { checkVersionIntervalMS } from '../../../config/component';
import { isNotNil, isObjectWithKeys } from '../../../services/guards';
import useSelfUpdatingRef from '../../../services/hooks/useSelfUpdatingRef';

import Body from '../../molecules/Body';
import CTAButton from '../../molecules/CTAButton';
import Header from '../../molecules/Header';
import Loading from '../../molecules/Loading';
import ConfirmationModal from '../ConfirmationModal';

type VersionEndpointJSONShape = Readonly<{
  service: string;
  git: Readonly<{
    commit: string;
  }>;
}>;

type VersionState = Readonly<{
  version: string;
  confirmed: boolean;
  shouldRefreshImmediately: boolean;
}>;

type Props = Readonly<{
  children: ReactNode;
  config: Config;
}>;

const RELOAD_VERSION_KEY = 'appVersionGuard:reloadedForVersion';
const SKIP_WAITING_TIMEOUT_MS = 3000;

const clearBrowserCaches = async (): Promise<void> => {
  try {
    if ('caches' in window) {
      const cacheNames = await caches.keys();
      await Promise.all(cacheNames.map((name) => caches.delete(name)));
    }
  } catch {
    // Best-effort — failure should not block the reload
  }
};

// Without this, a still-active old service worker keeps serving the precached
// old index.html on reload, so users see the same version forever.
const activateWaitingServiceWorker = async (): Promise<void> => {
  if (!('serviceWorker' in navigator)) {
    return;
  }

  try {
    const registration = await navigator.serviceWorker.getRegistration();
    if (isNil(registration)) {
      return;
    }

    if (isNil(registration.waiting)) {
      try {
        await registration.update();
      } catch {
        // Best-effort
      }
    }

    const { waiting } = registration;
    if (isNil(waiting)) {
      // No new SW available — drop the registration so the upcoming reload
      // hits the network directly instead of the still-active old worker.
      await registration.unregister().catch(() => undefined);
      return;
    }

    let controllerChanged = false;
    const controllerChangePromise = new Promise<void>((resolve) => {
      navigator.serviceWorker.addEventListener(
        'controllerchange',
        () => {
          controllerChanged = true;
          resolve();
        },
        { once: true },
      );
    });

    waiting.postMessage({ type: 'SKIP_WAITING' });

    await Promise.race([
      controllerChangePromise,
      new Promise<void>((resolve) => setTimeout(resolve, SKIP_WAITING_TIMEOUT_MS)),
    ]);

    if (!controllerChanged) {
      await registration.unregister().catch(() => undefined);
    }
  } catch {
    // Best-effort — failure should not block the reload
  }
};

const cleanUpVersionParam = (): void => {
  const url = new URL(window.location.href);

  if (url.searchParams.has('_v')) {
    url.searchParams.delete('_v');
    window.history.replaceState(null, '', url.toString());
  }
};

const AppVersionGuard = ({ children, config }: Props): ReactElement => {
  const { t } = useTranslation();

  const intervalDelayMS = useRef(checkVersionIntervalMS.focused);
  const [initialCheck, setInitialCheck] = useState(false);

  const [versionState, setVersionState] = useState<VersionState>({
    version: config.version,
    confirmed: true,
    shouldRefreshImmediately: false,
  });

  const onReload = async (version = versionState.version) => {
    const reloadedForVersion = sessionStorage.getItem(RELOAD_VERSION_KEY);
    sessionStorage.setItem(RELOAD_VERSION_KEY, version);
    await clearBrowserCaches();
    await activateWaitingServiceWorker();

    if (reloadedForVersion === version) {
      // Normal reload didn't pick up the new version — force a full navigation to bypass HTTP cache
      const url = new URL(window.location.href);
      url.searchParams.set('_v', version);
      window.location.replace(url.toString());
    } else {
      window.location.reload();
    }
  };

  const onClose = () => {
    setVersionState((prev) => ({ ...prev, confirmed: true }));
  };

  const checkIfUpToDateRef = useSelfUpdatingRef(async (shouldRefreshImmediately = false) => {
    const appVersionInfo = await fetch(config.appVersionStatusUrl, { cache: 'no-cache' })
      .then(async (response) => response.json())
      .then((data) => {
        if (!isObjectWithKeys<VersionEndpointJSONShape>(data, ['git', 'service'])) {
          return null;
        }

        if (isObjectWithKeys<VersionEndpointJSONShape['git']>(data.git, ['commit'])) {
          return data;
        }

        return null;
      })
      .catch(() => null);
    setInitialCheck(true);

    if (isNil(appVersionInfo)) {
      return;
    }

    const serverVersion = appVersionInfo.git.commit;

    if (serverVersion === config.version && isNotNil(sessionStorage.getItem(RELOAD_VERSION_KEY))) {
      sessionStorage.removeItem(RELOAD_VERSION_KEY);
      cleanUpVersionParam();
    }

    setVersionState((prev) => (serverVersion === prev.version ? prev : {
      version: serverVersion,
      confirmed: false,
      shouldRefreshImmediately,
    }));
  });

  const checkVersionPeriodicallyRef = useSelfUpdatingRef(async () => {
    await new Promise((resolve) => setTimeout(resolve, intervalDelayMS.current));
    await checkIfUpToDateRef.current();

    void checkVersionPeriodicallyRef.current();
  });

  const isReloading = useRef(false);

  useEffect(() => {
    if (versionState.shouldRefreshImmediately && !isReloading.current) {
      isReloading.current = true;
      void onReload(versionState.version);
    }
  }, [versionState.shouldRefreshImmediately, versionState.version]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    void checkIfUpToDateRef.current();

    const onFocus = () => {
      void checkIfUpToDateRef.current();
      intervalDelayMS.current = checkVersionIntervalMS.focused;
    };

    const onBlur = () => {
      intervalDelayMS.current = checkVersionIntervalMS.blurred;
    };

    window.addEventListener('focus', onFocus);
    window.addEventListener('blur', onBlur);

    void checkVersionPeriodicallyRef.current();

    return () => {
      window.removeEventListener('focus', onFocus);
      window.removeEventListener('blur', onBlur);
    };
  }, [checkIfUpToDateRef, checkVersionPeriodicallyRef]);

  if (!initialCheck || isReloading.current) {
    return <Loading />;
  }

  return (
    <>
      {!versionState.confirmed && (
        <ConfirmationModal
          header={<Header as="h4">{t('We have just updated the app')}</Header>}
          body={<Body size={300}>{t('Reload the page to apply these updates. If the problem persists, clear your browser\'s cache.')}</Body>}
          onConfirm={() => onReload()}
          onDeny={onClose}
          confirmButton={(
            <CTAButton
              onClick={() => onReload()}
              ref={(node) => node?.focus()}
            >
              {t('Reload')}
            </CTAButton>
          )}
        />
      )}
      {children}
    </>
  );
};

export default AppVersionGuard;
